import { requireOptionalNativeModule } from 'expo-modules-core';
import { NativeModules, Platform } from 'react-native';

type NativeLocale = { languageTag?: string | null; regionCode?: string | null };
type NativeCalendar = { firstWeekday?: number | null };
type NativeLocalizationModule = {
  getLocales?: () => NativeLocale[];
  getCalendars?: () => NativeCalendar[];
};

function getExpoLocalization(): NativeLocalizationModule | undefined {
  try {
    return requireOptionalNativeModule<NativeLocalizationModule>('ExpoLocalization') ?? undefined;
  } catch {
    return undefined;
  }
}

// Yeni binary expo-localization taşır. Eski 1.0.5 binary bir OTA aldığında native
// modül henüz yoktur; optional native lookup açılış çökmesi yerine aşağıdaki
// Intl/NativeModules geri dönüşlerinin çalışmasını sağlar.
function getExpoLocale(): NativeLocale | undefined {
  return getExpoLocalization()?.getLocales?.()[0];
}

// Cihazın ham locale dizesini döndürür (ör. 'en-US', 'tr-TR', 'de_DE').
// Öncelik: Expo'nun platformlar arası native locale katmanı. Eski/olağandışı
// çalışma ortamlarında Hermes Intl ve React Native native modülleri yedektir.
function getRawLocale(): string {
  try {
    const locale = getExpoLocale();
    if (locale?.languageTag) return locale.languageTag;
  } catch {}
  try {
    const l = Intl.DateTimeFormat().resolvedOptions().locale;
    if (l) return l;
  } catch {}
  try {
    if (Platform.OS === 'ios') {
      const s: any = NativeModules.SettingsManager?.settings;
      return s?.AppleLocale || s?.AppleLanguages?.[0] || 'en';
    }
    return NativeModules.I18nManager?.localeIdentifier || 'en';
  } catch {}
  return 'en';
}

// Cihazın dil kodunu döndürür (ör. 'en', 'tr', 'de').
// i18n (#25) gelince oradan beslenebilir; şimdilik cihaz ayarından okur.
export function normalizeDisplayLanguage(raw: string): string {
  const normalized = raw.trim().replace(/_/g, '-').toLowerCase();
  const base = normalized.split('-')[0];
  if (base === 'pt') return 'pt-BR';
  // Geleneksel Çinceyi yanlışlıkla Basitleştirilmiş Çince diye göstermeyiz.
  // zh-CN/zh-SG ve açık Hans etiketi desteklenen zh-Hans sözlüğüne gider;
  // Hant/TW/HK/MO ise zh-Hant eklenene kadar güvenli İngilizce geri dönüşüdür.
  if (base === 'zh') {
    if (normalized.includes('hant') || /-(tw|hk|mo)(-|$)/.test(normalized)) return 'en';
    return 'zh-Hans';
  }
  // Android'in eski Indonesian dil kodu.
  if (base === 'in') return 'id';
  return ['tr', 'en', 'es', 'fr', 'de', 'id', 'ru', 'ja', 'hi', 'bn'].includes(base)
    ? base
    : 'en';
}

export function getDeviceLanguage(): string {
  return normalizeDisplayLanguage(getRawLocale());
}

/** Raw device locale for a suggestion, never an inferred user preference. */
export function getDeviceLocale(): string { return getRawLocale().replace(/_/g, '-'); }

// React Native'in `textTransform: 'uppercase'` dönüşümü locale bilgisini
// kullanmaz; Türkçede "Fiyatlar"ı "FIYATLAR" yapması bunun görünür örneği.
// Bölüm etiketleri çizilmeden önce seçili görüntüleme dilinde dönüştürülür.
export function uppercaseForDisplayLanguage(value: string, language: string): string {
  try {
    return value.toLocaleUpperCase(language);
  } catch {
    return value.toUpperCase();
  }
}

// Push tarih/saatleri sağlayıcıya gönderilmeden önce sunucuda biçimlenir. Dil
// gibi saat dilimi de token'a (cihaza) aittir; aynı hesap İstanbul ve Londra'daki
// iki cihazda kendi yerel saatini görmelidir. Intl yoksa tahmin üretmeyiz.
export function getDeviceTimeZone(): string | null {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof timeZone !== 'string' || !timeZone.trim()) return null;
    const normalized = timeZone.trim();
    // Sabit ofset bölgesel yaz-saati ve tarih kurallarını taşımaz; sunucuya
    // IANA kaynak bölgesi diye gönderilmez.
    return /^[+-]\d{2}:\d{2}$/.test(normalized) ? null : normalized;
  } catch {
    return null;
  }
}

// Cihazın bölge (ülke) kodunu döndürür (ör. 'US', 'TR', 'GB'); yoksa ''.
// Telefon giriş ekranı varsayılan ülke kodunu buradan türetir — küresel ürün,
// sabit tek varsayılan yok. 'en' gibi bölgesiz locale'de '' döner (fallback).
export function getDeviceRegion(): string {
  try {
    const region = getExpoLocale()?.regionCode;
    if (region && /^[A-Za-z]{2}$/.test(region)) return region.toUpperCase();
  } catch {}
  const parts = getRawLocale().split(/[-_]/);
  if (parts.length < 2) return '';
  const last = parts[parts.length - 1];
  return /^[A-Za-z]{2}$/.test(last) ? last.toUpperCase() : '';
}

// Takvim satırı ülkeye göre Pazar veya Pazartesi gibi farklı bir günde
// başlamalıdır. Expo 1=Pazar … 7=Cumartesi döndürür; React Native Date ise
// 0=Pazar … 6=Cumartesi kullanır. Eski binary native metodu taşımıyorsa Intl
// weekInfo denenir, o da yoksa önceki davranış olan Pazartesi korunur.
export function getDeviceFirstWeekday(): number {
  try {
    const nativeDay = getExpoLocalization()?.getCalendars?.()[0]?.firstWeekday;
    if (typeof nativeDay === 'number' && nativeDay >= 1 && nativeDay <= 7) {
      return nativeDay - 1;
    }
  } catch {}
  try {
    const Locale = (Intl as any).Locale;
    const locale = Locale ? new Locale(getRawLocale()) : null;
    const intlDay = locale?.weekInfo?.firstDay ?? locale?.getWeekInfo?.()?.firstDay;
    // Intl Locale: 1=Pazartesi … 7=Pazar; Date: 0=Pazar … 6=Cumartesi.
    if (typeof intlDay === 'number' && intlDay >= 1 && intlDay <= 7) {
      return intlDay % 7;
    }
  } catch {}
  return 1;
}
